import { ArrowLeft } from "lucide-react";
import type React from "react";
import { useSmartBack } from "@/lib/useSmartBack";

/**
 * Page header — barra clara no topo de páginas de detalhe.
 *
 * Padrão: paper background, ícone do contexto, label maiúscula, título médio,
 * status pill opcional, ações à direita. Usado em Resultado, KbDetail,
 * Consumo (com variações). Substitui o "hero preto" antigo.
 */

export interface PageHeaderProps {
  /** Rota de FALLBACK do "Voltar" (usada só se a pessoa entrou por link direto,
   *  sem histórico). No fluxo normal, o "Voltar" volta pra página anterior. Sem
   *  este prop, o botão some. */
  backTo?: string;
  backLabel?: string;
  /** Ícone de contexto que aparece no badge laranja à esquerda */
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  /** Label kicker em maiúsculo (ex: "ENTREGÁVEL", "CONSUMO") */
  kicker?: React.ReactNode;
  /** Título principal da página */
  title: React.ReactNode;
  /** Status pill ao lado do kicker, se houver */
  statusBadge?: {
    label: string;
    bg: string;
    fg: string;
  } | null;
  /** Conteúdo extra à direita (botões, period selector etc) */
  rightSlot?: React.ReactNode;
  /** Constrange max-width do conteúdo (default 1180) */
  maxWidth?: number;
}

export function PageHeader({
  backTo,
  backLabel = "Voltar",
  icon: IconCmp,
  kicker,
  title,
  statusBadge,
  rightSlot,
  maxWidth = 1180,
}: PageHeaderProps) {
  const goBack = useSmartBack(backTo ?? "/painel");
  return (
    <div
      style={{
        background: "var(--wf-paper)",
        borderBottom: "1px solid var(--wf-ink-5)",
        padding: "16px 24px",
      }}
    >
      <div
        style={{
          maxWidth,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {backTo && (
          <button
            type="button"
            onClick={goBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              padding: "5px 12px 5px 8px",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 999,
              background: "var(--wf-paper)",
              color: "var(--wf-ink-2)",
              cursor: "pointer",
              fontFamily: "var(--wf-font-body)",
              fontWeight: 500,
            }}
          >
            <ArrowLeft size={12} strokeWidth={2} /> {backLabel}
          </button>
        )}
        {IconCmp && (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "rgba(255,112,0,0.10)",
              color: "var(--nd-orange)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconCmp size={16} strokeWidth={1.75} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {(kicker || statusBadge) && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "var(--wf-ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 2,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {kicker && <span>{kicker}</span>}
              {statusBadge && (
                <span
                  style={{
                    padding: "1px 8px",
                    borderRadius: 999,
                    background: statusBadge.bg,
                    color: statusBadge.fg,
                    letterSpacing: "0.04em",
                    fontSize: 10,
                  }}
                >
                  {statusBadge.label}
                </span>
              )}
            </div>
          )}
          <h1
            className="wf-display"
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "-0.015em",
              lineHeight: 1.2,
              margin: 0,
              color: "var(--wf-ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </h1>
        </div>
        {rightSlot}
      </div>
    </div>
  );
}
